/// <reference types="vite/client" />

import type { AxiosError, AxiosRequestConfig } from 'axios'
import axios, { isAxiosError } from 'axios'
import * as R from 'remeda'

import { sessionStore } from '@gto/session'

import { getRuntimeConfig } from '@/config/runtime-env'

const baseURL = getRuntimeConfig().apiBaseUrl

export const axiosInstance = axios.create({
  baseURL,
})

let isRefreshing = false
let failedRequests: { resolve: (value: unknown) => void; reject: (reason?: unknown) => void }[] = []

const processQueue = () => {
  failedRequests.forEach(({ resolve }) => {
    resolve(true)
  })
  failedRequests = []
}

axiosInstance.interceptors.request.use((request) => {
  const accessToken = sessionStore.getState().accessToken
  const lang = localStorage.getItem('lang') ?? 'en'

  if (R.isNonNullish(accessToken) && !R.isEmpty(accessToken)) {
    request.headers.Authorization = `Bearer ${accessToken}`
  }

  request.headers['Accept-Language'] = lang

  // request.headers.GWCLIENTID = sessionStore.getState().appUid

  /** Removes square brackets [] from query params
   * I.e before it was ?ordering[]=total_hands&ordering[]=original_name
   * Now it is ?ordering=total_hands&ordering=original_name
   */
  request.paramsSerializer = {
    indexes: null,
  }

  return request
})

axiosInstance.interceptors.response.use(
  (request) => request,
  async (error: AxiosError | string) => {
    if (isAxiosError(error)) {
      const originalRequest = error.config
      if (
        originalRequest &&
        originalRequest.url !== '/api/v1/token/' &&
        error.response?.status === 401 &&
        !(
          typeof error.response.data === 'object' &&
          error.response.data &&
          'error_code' in error.response.data &&
          error.response.data.error_code === 'EMAIL_NOT_VERIFIED'
        )
      ) {
        if (isRefreshing) {
          try {
            await new Promise((resolve, reject) => {
              failedRequests = [...failedRequests, { resolve, reject }]
            })

            return await axiosInstance(originalRequest)
          } catch (e) {
            return e
          }
        }

        try {
          isRefreshing = true
          const refresh = localStorage.getItem('user_refresh')

          const response: { data: { access: string } } = await axios.post(
            `${baseURL}/api/v1/token/refresh/`,
            R.isNonNullish(refresh)
              ? {
                  refresh,
                }
              : undefined,
          )

          const { access } = response.data
          sessionStore.getState().setAccessToken(access)
          axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`
          isRefreshing = false
          processQueue()
          //Refreshing token failed
        } catch (error) {
          isRefreshing = false
          handleAuthFailure(error)

          return Promise.reject(new Error('Token refresh failed'))
        }

        try {
          return await axiosInstance(originalRequest)
          //Original request failed
        } catch (error) {
          // Only handle auth failure if the refresh token is still invalid
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            handleAuthFailure(error)

            return Promise.reject(new Error('Token refresh failed'))
          }

          return error instanceof Error
            ? Promise.reject(error)
            : Promise.reject(new Error('Original request failed after the token was refreshed'))
        }
      }

      if (error.response?.status === 403 && !window.location.pathname.includes('/blocked')) {
        if (
          !(
            typeof error.response.data === 'object' &&
            error.response.data &&
            'error_code' in error.response.data &&
            error.response.data.error_code === 'MIGRATION_REQUIRED'
          )
        ) {
          window.location.href = '/blocked'
        }
      }

      return Promise.reject(error)
    }
  },
)

const handleAuthFailure = (error: unknown) => {
  if (isAxiosError(error)) {
    if (error.response?.status === 403 && !window.location.pathname.includes('/blocked')) {
      window.location.href = '/blocked'
    }
  }
  localStorage.removeItem('user_refresh')
  sessionStore.getState().clearAccessToken()
}
export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> =>
  axiosInstance<T>({ ...config }).then(({ data }) => data)

export default customInstance
